import React, { memo, useEffect, useState, useRef } from 'react';
import { ExclamationCircleFilled, UserOutlined } from '@ant-design/icons';
import { ConfigProvider, Button, Checkbox, Form, Input, Typography, Space } from 'antd';
import { createLeadForm } from '@/services/lead';
import Icon from '@ant-design/icons';
import ArrowRight from '@/public/assets/arrow-right.svg';
import SubmitSuccessIcon from '@/public/icon/submit_success.svg';
import { useLanguage } from '@/hooks/use-language';

import styles from './ContactForm.module.scss';

const { Text, Link } = Typography;

const ContactForm = memo(() => {
  const [form] = Form.useForm();
  const inputRef = useRef(null);
  const [hasSubmitted, setHasSubmitted] = useState<any>(false);
  const [phoneVal, setPhoneVal] = useState<any>(null);
  const [isFocused, setIsFocused] = useState<any>(false);
  const [screenWidth, setScreenWidth] = useState<any>(0);
  const [loading, setLoading] = useState<any>(false);
  const language = useLanguage();

  const onFinish = async (values: any) => {
    form.validateFields().then(async (val: any) => {
      setLoading(true);
      const repsonse = await createLeadForm({
        name: values.name,
        email: values.email,
        marketingOptIn: true,
        dropOffStage: 'Blog',
        mobile: values.mobile?.replace('+856 ', '') ?? '',
        language: language ? language : 'zh'
      });
      if (repsonse?.data?.success) {
        setHasSubmitted(true);
        setLoading(false);
        const expiry = (new Date().getTime() + 1000 * 60 * 60 * 24) / 1000;
        localStorage.setItem(
          'createdLeadForm',
          JSON.stringify({ expiry: expiry, hasSubmitted: true })
        );
      }
      setLoading(false);
    });
  };

  useEffect(() => {
    const expiryFromStorage =
      localStorage.getItem('createdLeadForm') &&
      JSON?.parse(localStorage.getItem('createdLeadForm') as any);
    const isNotLeadFormExpiry = expiryFromStorage?.expiry * 1000 > new Date().getTime();
    setHasSubmitted(isNotLeadFormExpiry ? true : false);
    setScreenWidth(window.innerWidth);
  }, []);

  const handleChangeTelephone = (e: any) => {
    setPhoneVal(e.target.value.replace(/[^\-?\d.]/g, ''));
    form.setFieldsValue({ mobile: e.target.value.replace(/[^\-?\d.]/g, '') });
    const inputElement = inputRef?.current as any;
    setTimeout(() => {
      const mobileTrimmedLength = e.target.value.replace(/(^\s*)|(\s*$)/g, '').length;
      // according to the mask of mobile phone, when types four numbers, there must be a space after four numbers.
      const mobileSelectionPosition = mobileTrimmedLength === 4 ? 5 : mobileTrimmedLength;
      // eslint-disable-next-line
      inputElement && inputElement?.focus();
      // eslint-disable-next-line
      inputElement &&
        inputElement?.setSelectionRange(mobileSelectionPosition, mobileSelectionPosition);
    }, 50);
  };

  const onFocusEvent = () => {
    setIsFocused(true);
    const inputElement = inputRef?.current as any;
    setTimeout(() => {
      const mobileVal = form.getFieldValue('mobile');
      const mobileTrimmedLength = mobileVal?.replace(/(^\s*)|(\s*$)/g, '')?.length;
      // according to the mask of mobile phone, when types four numbers, there must be a space after four numbers.
      const mobileSelectionPosition = mobileTrimmedLength === 4 ? 5 : mobileTrimmedLength;
      inputElement?.focus();
      inputElement &&
        inputElement?.setSelectionRange(mobileSelectionPosition, mobileSelectionPosition);
    }, 50);
  };

  const validatePhoneNumber = (rule: any, value: any) => {
    const phoneRegex = /^([4-9]{1})([0-9]{3})([0-9]{4})/;
    if (value && !phoneRegex.test(value.replace('+852 ', ''))) {
      // <ExclamationCircleFilled />;
      return Promise.reject('請輸入有效的電話號碼');
    }
    return Promise.resolve();
  };

  const isEmail = (value: string) => {
    const emailRegex =
      /^[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;
    const emailParts = value.split('@');

    if (emailParts.length !== 2) return false;

    const account = emailParts[0];
    const address = emailParts[1];

    if (account.length > 64) return false;

    if (address.length > 255) return false;

    const domainParts = address.split('.');

    if (domainParts.some(part => part.length > 63)) return false;

    return emailRegex.test(value);
  };

  const validateEmail = (rule: any, value: any) => {
    if (!value || isEmail(value)) {
      return Promise.resolve();
    }
    return Promise.reject('您輸入的電郵地址不正確');
  };

  const validatePermission = (rule: any, value: any) => {
    if (value) {
      return Promise.resolve();
    }
    return Promise.reject('請確認並繼續');
  };

  const validateMarketInOptIn = (rule: any, value: any) => {
    if (value) {
      return Promise.resolve();
    }
    return Promise.reject('請確認並繼續');
  };

  return (
    <div className={styles.root_form_wrapper}>
      {hasSubmitted && (
        <div className={styles.contact_form_success_wrapper}>
          <Icon component={SubmitSuccessIcon} className={styles.contact_form_success_icon} />
          <div>
            <div className={styles.contact_form_success_title}>感謝您的訂閱！</div>
            <div className={styles.contact_form_success_desc}>
              您將會很快收到我們的電郵，敬請期待！
            </div>
          </div>
        </div>
      )}
      {!hasSubmitted && (
        <div className={styles.contact_form_wrapper}>
          <div className={styles.contact_form_title}>不想錯過最新資訊？</div>
          <div className={styles.contact_form_description}>
            訂閱我們的網誌，可以定期獲得熱門文章推薦及富衛推廣訊息或資料！
          </div>
          <ConfigProvider
            theme={{
              components: {
                Form: {
                  labelColor: '#fff',
                  colorText: '#fff'
                }
              }
            }}
          >
            <Form
              form={form}
              className={styles.contact_form}
              initialValues={{ remember: true }}
              onFinish={onFinish}
              layout="inline"
            >
              <Form.Item
                name="name"
                style={{ width: screenWidth > 600 ? 'calc(50% - 16px)' : '100%' }}
                rules={[{ required: true, message: '請輸入姓名' }]}
              >
                <Input placeholder="姓名" variant="borderless" maxLength={40} />
              </Form.Item>
              <Form.Item
                style={{
                  width: screenWidth > 600 ? 'calc(50% - 16px)' : '100%',
                  marginTop: screenWidth > 600 ? 0 : 16
                }}
                name="mobile"
                rules={[ { required: true, message: `請輸入電話號碼` },{ validator: validatePhoneNumber }]}
              >
                <Input
                  addonBefore={(phoneVal || isFocused) && <div>+852</div>}
                  placeholder="電話號碼"
                  variant="borderless"
                  onChange={handleChangeTelephone}
                  ref={inputRef}
                  maxLength={8}
                  onFocus={onFocusEvent}
                  onBlur={() => setIsFocused(false)}
                />
              </Form.Item>
              <Form.Item style={{ width: '100%', marginTop: 16 }}>
                <Form.Item
                  name="email"
                  rules={[
                    { required: true, message: `請輸入電子郵件` },
                    { validator: validateEmail }
                  ]}
                >
                  <Input placeholder="電郵" variant="borderless" maxLength={80} />
                </Form.Item>
              </Form.Item>
              <Form.Item style={{ width: '100%', marginTop: 24 }}>
                <Form.Item
                  name="oneTimeOptIn"
                  valuePropName="checked"
                  noStyle
                  rules={[{ validator: validatePermission }]}
                >
                  <Checkbox>
                    本人已參閱及明白富衛金融有限公司（「富衛金融」）的
                    <a
                      href="https://www.fwd.com.hk/zh/disclaimer/"
                      target="_blank"
                      style={{
                        color: '#fff',
                        fontSize: '12px !important'
                      }}
                    >
                      <Text style={{ color: '#fff', fontSize: '12px !important' }} underline>
                        私隱政策
                      </Text>
                    </a>
                    、富衛金融的
                    <a
                      href="https://www.fwd.com.hk/zh/privacy-policy/fwd-financial-limited/"
                      target="_blank"
                      style={{
                        color: '#fff',
                        fontSize: '12px !important'
                      }}
                    >
                      <Text style={{ color: '#fff', fontSize: '12px !important' }} underline>
                        收集個人資料聲明
                      </Text>
                    </a>
                    及富衛人壽保險（百慕達）有限公司的
                    <a
                      href="https://www.fwd.com.hk/zh/privacy-policy/fwd-life-insurance-company/"
                      target="_blank"
                      style={{
                        color: '#fff',
                        fontSize: '12px !important'
                      }}
                    >
                      <Text style={{ color: '#fff', fontSize: '12px !important' }} underline>
                        收集個人資料聲明
                      </Text>
                    </a>
                    、及同意富衛金融收集及使用本人的個人資料作為聯絡本人及根據此表格提供上述之用途，並同意接受其約束。
                  </Checkbox>
                </Form.Item>
              </Form.Item>
              <Form.Item style={{ width: '100%', marginTop: 16 }}>
                <Form.Item
                  name="marketingOptIn"
                  valuePropName="checked"
                  noStyle
                  rules={[{ validator: validateMarketInOptIn }]}
                >
                  <Checkbox>
                    <span style={{ lineHeight: '20px !important' }}>
                      本人同意
                      <a
                        href="https://www.fwd.com.hk/zh/privacy-policy/fwd-life-insurance-company/"
                        target="_blank"
                        style={{
                          color: '#fff',
                          fontSize: '12px !important',
                          lineHeight: '20px !important'
                        }}
                      >
                        <Text style={{ color: '#fff', fontSize: '12px !important' }} underline>
                          富衛人壽保險（百慕達）有限公司
                        </Text>
                      </a>
                      及
                      <a
                        href="https://www.fwd.com.hk/zh/privacy-policy/fwd-financial-limited/"
                        target="_blank"
                        style={{
                          color: '#fff',
                          fontSize: '12px !important'
                        }}
                      >
                        <Text style={{ color: '#fff', fontSize: '12px !important' }} underline>
                          富衛金融
                        </Text>
                      </a>
                      根據其收集個人資料聲明的第九項使用及轉移本人的個人資料作為直接促銷用途，及接收推廣訊息或資料。
                    </span>
                  </Checkbox>
                </Form.Item>
              </Form.Item>
              <Form.Item style={{ width: '100%' }}>
                <ConfigProvider
                  theme={{
                    components: {
                      Button: {
                        fontWeight: 700,
                        contentFontSize: 16,
                        defaultColor: '#E87722',
                        defaultBorderColor: '#E87722',
                        defaultBg: '#ffffff',
                        paddingBlock: 10
                      }
                    }
                  }}
                >
                  <Button htmlType="submit" className={styles.submit_button} loading={loading}>
                    <Icon component={ArrowRight} className={styles.submit_button_icon} />
                    立即訂閱
                  </Button>
                </ConfigProvider>
              </Form.Item>
            </Form>
          </ConfigProvider>
        </div>
      )}
    </div>
  );
});

export default ContactForm;
